// src/utils/upiQr.js
// ─────────────────────────────────────────────────────────────────────────────
// Dynamic UPI payment-QR generation for invoices (used by the WhatsApp send).
//
// Builds the `upi://pay?...` deep link for an invoice amount and renders it as
// a high-resolution PNG canvas with the brand logo centered on a white plate.
// Error correction level "H" keeps the code scannable while the logo occludes
// ~20% of its area.
//
// The image is generated in the browser, handed to the send-upi-qr WhatsApp API
// route and discarded — it is NEVER uploaded to storage or written to
// Firestore (no persistence by design).
// ─────────────────────────────────────────────────────────────────────────────

// Fallbacks used when settings/branding has no UPI values yet.
const DEFAULT_UPI_ID = "boism-9825156797@boi";
const DEFAULT_UPI_NAME = "Nilam Synthetics";

// `@` is legal unencoded in a URI query (RFC 3986 pchar) and UPI apps expect
// `pa=merchant@bank`, so we keep it raw while encoding everything else
// (spaces → %20 for pn/tn, matching the reference format).
const encodeQueryValue = (value) =>
  encodeURIComponent(String(value ?? "")).replace(/%40/gi, "@");

/** Format an amount exactly as UPI expects: "499.00" (always 2 decimals). */
export const formatUpiAmount = (amount) =>
  (Math.round((Number(amount) || 0) * 100) / 100).toFixed(2);

/**
 * Build the UPI deep link for an invoice.
 *
 * @param {Object} p
 * @param {string} p.pa  Payee VPA (UPI ID), required.
 * @param {string} p.pn  Payee (merchant) name, required.
 * @param {string} p.am  Amount, already formatted ("499.00").
 * @param {string} [p.cu="INR"] Currency.
 * @param {string} [p.tn]       Transaction note — invoice number.
 * @param {string} [p.tr]       Transaction reference ID — invoice number.
 * @param {string} [p.mc]       Merchant category code (optional; omitted when empty).
 * @returns {string} e.g. upi://pay?pa=merchant@bank&pn=Merchant%20Name&am=499.00&cu=INR&tn=...&tr=...
 */
export const buildUpiUri = ({ pa, pn, am, cu = "INR", tn = "", tr = "", mc = "" }) => {
  if (!pa) throw new Error("UPI ID (pa) is required to build a payment QR");
  const params = [
    `pa=${encodeQueryValue(pa)}`,
    `pn=${encodeQueryValue(pn || "")}`,
    `am=${encodeQueryValue(am)}`,
    `cu=${encodeQueryValue(cu || "INR")}`,
  ];
  if (tn) params.push(`tn=${encodeQueryValue(tn)}`);
  if (tr) params.push(`tr=${encodeQueryValue(tr)}`);
  if (mc) params.push(`mc=${encodeQueryValue(mc)}`);
  return `upi://pay?${params.join("&")}`;
};

// Load an image for canvas compositing (CORS-safe so a Cloudinary logo does
// not taint the canvas and block toBlob()).
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load logo image: ${src}`));
    img.src = src;
  });

// Rounded-rect path without ctx.roundRect (not available in older browsers).
const fillRoundedRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
};

/**
 * Generate the payment-QR PNG for an invoice.
 *
 * @param {Object} params
 * @param {number|string} params.amount      Invoice grand total (e.g. 499).
 * @param {string} params.invoiceNumber      Invoice (bill) number → tn + tr.
 * @param {Object} [params.branding]         Branding (upiId, upiName, upiMcc, logoUrl).
 * @returns {Promise<Blob>} PNG blob of the QR image.
 */
export const generateUpiQrImage = async ({ amount, invoiceNumber, branding = {} }) => {
  // Dynamically import so the QR encoder is code-split (same pattern as
  // html2canvas/jsPDF in utils/pdf.js).
  const QRCode = (await import("qrcode")).default;

  const pa = String(branding.upiId || DEFAULT_UPI_ID).trim();
  const pn = String(branding.upiName || "").trim() || DEFAULT_UPI_NAME;
  const invoice = String(invoiceNumber || "").trim();
  const uri = buildUpiUri({
    pa,
    pn,
    am: formatUpiAmount(amount),
    cu: "INR",
    tn: invoice,
    tr: invoice,
    mc: String(branding.upiMcc || "").trim(),
  });

  // 1. Render the QR at high resolution (black on white for max scannability).
  const canvas = document.createElement("canvas");
  await QRCode.toCanvas(canvas, uri, {
    errorCorrectionLevel: "H",
    margin: 4, // spec quiet zone
    width: 1024,
    color: { dark: "#000000", light: "#ffffff" },
  });

  // 2. Center the brand logo on a white plate (≈25% of the QR width — well
  //    within level-H recovery) so the modules underneath don't scan as noise.
  const logoSources = [branding.logoUrl, "/icons/icon-192.png"].filter(Boolean);
  let logo = null;
  for (const src of logoSources) {
    try {
      logo = await loadImage(src);
      break;
    } catch (e) {
      // try the next candidate (e.g. an expired remote URL → local fallback)
    }
  }

  if (logo) {
    const ctx = canvas.getContext("2d");
    const plate = Math.round(canvas.width * 0.28);
    const icon = Math.round(plate * 0.78);
    const px = Math.round((canvas.width - plate) / 2);
    const py = Math.round((canvas.height - plate) / 2);

    ctx.fillStyle = "#ffffff";
    fillRoundedRect(ctx, px, py, plate, plate, Math.round(plate * 0.18));
    ctx.drawImage(logo, Math.round((canvas.width - icon) / 2), Math.round((canvas.height - icon) / 2), icon, icon);
  }

  // 3. Encode to PNG.
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Failed to encode the QR image"))),
      "image/png"
    );
  });
};
